import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { toast } from 'react-toastify';
// For reasons we can't figure out, eslint can't handle these modules. TS can compile it fine.
import * as WebDevices from 'web-device-mux';
import * as WebLabel from 'webzlp';
import { BadgeData, BadgeDesign, DesignerNode } from '../../../../shared/user/badgeDesign';
import { BadgePageRender, transformNodeMap } from '../../../modules/badgerenderer/BadgeRender';
import { store } from '../../../services';
import { TranslationProvider } from '../../../translations';
import { delayPromise } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import * as PrinterBase from '../printers/PrinterBase';
import {
  LabelPrinterInfo,
  PrintBadgeData,
  ZebraAction,
  ZebraSaveStatus,
  ZebraState,
} from './reducer';

type PrinterManager = WebDevices.UsbDeviceManager<WebLabel.LabelPrinterUsb>;
type PrinterManagerEventMap = WebDevices.IUsbDeviceManagerEventMap<WebLabel.LabelPrinterUsb>;

async function readFont(blob: Blob): Promise<string> {
  return await new Promise((resolve) => {
    const reader = new FileReader();

    reader.onloadend = function onloadend() {
      resolve(reader.result as string);
    };

    reader.readAsDataURL(blob);
  });
}

interface FontBlob {
  name: string;
  blob: string;
}

async function getFonts(name: string): Promise<FontBlob[]> {
  const fetch1 = await fetch(`https://fonts.googleapis.com/css?family=${name}`);
  const fetchText = await fetch1.text();
  const fontLocations = fetchText.match(/https:\/\/[^)]+/g);

  if (!fontLocations) {
    return [];
  }

  const result: FontBlob[] = [];

  for (const loc of fontLocations) {
    const fetch2 = await fetch(loc);
    const blob = await fetch2.blob();
    const font = await readFont(blob);
    result.push({ blob: font, name });
  }

  return result;
}

async function getSVGFontStyle(nodes: DesignerNode[]) {
  const fonts: FontBlob[] = [];

  for (const node of nodes) {
    if (node.type !== 'text') {
      continue;
    }

    try {
      const fontName = node.fontFamily === 'custom' ? node.customFontFamily! : node.fontFamily;
      const fontBlobs = await getFonts(fontName);
      for (const font of fontBlobs) {
        fonts.push(font);
      }
    } catch (error) {
      // Just a warning. Do not block drawing the badge.
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  }

  const fontFaces = fonts.map((f) => {
    return `@font-face {
      font-family: "${f.name}";
      src: url("${f.blob}") format("woff2");
    }`;
  });

  return `<style type="text/css">${fontFaces.join('\n')}</style>`;
}

export class ZebraPrinter2 {
  public type = PrinterBase.PrinterType.WebLabel;
  private readonly mgr?: PrinterManager;

  public constructor(
    public dispatch: (action: ZebraAction) => void,
    _saved?: ZebraSaveStatus,
  ) {
    // This is a placeholder for the ZebraPrinter2 class.
    // It can be used to dispatch actions related to Zebra printing.

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.navigator.usb) {
      // TODO: Better handling for non-WebUSB browsers
      this.mgr = undefined;
      this.setStatus(PrinterBase.PrinterManagerStatus.Error);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (window.zebraPrinterManager) {
      this.mgr = window.zebraPrinterManager;
      return;
    }

    this.mgr = new WebDevices.UsbDeviceManager(
      window.navigator.usb,
      async (device, options) => await WebLabel.LabelPrinter.fromUSBDevice(device, options),
      {
        requestOptions: {
          filters: [
            {
              // Zebra
              vendorId: 0x0a_5f,
            },
          ],
        },
        /*
         * Print debug messages to the console
         * TODO: This is very chatty!
         */
        debug: true,
      },
    );

    this.mgr
      .forceReconnect()
      .then(() => {
        this.setStatus(PrinterBase.PrinterManagerStatus.Ready);
      })
      .catch((error: Error) => {
        toast.error(error.message);
        this.setStatus(PrinterBase.PrinterManagerStatus.Error);
      });

    window.zebraPrinterManager = this.mgr;
  }

  public getSaveStatus(state: ZebraState): ZebraSaveStatus {
    return {
      primaryPrinter: state.primaryPrinter,
      secondaryPrinter: state.secondaryPrinter,
    };
  }

  public setStatus(status: PrinterBase.PrinterManagerStatus): void {
    // This method is a placeholder for setting the state of the Zebra printer.
    // It can be used to save the state of the Zebra printer.
    this.dispatch({
      type: 'ZEBRA_SET_PRINTER_STATE',
      status,
      source: 'zebra',
    });
  }

  public testPrint(serial: string): void {
    const printer = this.mgr?.devices.find((p) => p.printerSerial === serial);
    if (printer) {
      this._testPrint(printer).catch(captureError);
    }
  }

  public startManualConnect(): void {
    this.mgr?.promptForNewDevice().catch(captureError);
  }

  public getAvailablePrinters(): LabelPrinterInfo[] {
    return this.mgr?.devices.map((d) => this.toPrinterInfo(d)) ?? [];
  }

  public addEventListener<T extends keyof PrinterManagerEventMap>(
    type: T,
    listener:
      | EventListenerObject
      | ((this: PrinterManager, ev: PrinterManagerEventMap[T]) => void)
      | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    this.mgr?.addEventListener(type, listener, options);
  }

  public removeEventListener<T extends keyof PrinterManagerEventMap>(
    type: T,
    listener:
      | EventListenerObject
      | ((this: PrinterManager, ev: PrinterManagerEventMap[T]) => void)
      | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    this.mgr?.removeEventListener(type, listener, options);
  }

  public printBadge(data: PrintBadgeData): void {
    this.print(data.printerSerial, data.badge, data.design, data.reason);
  }

  public print(serial: string, badge: BadgeData, design: BadgeDesign, reason?: string): void {
    const printer = this.mgr?.devices.find((p) => p.printerSerial === serial);

    if (printer) {
      this._print(printer, badge, design, reason)
        .catch(captureError)
        .finally(() => {
          this.dispatch({
            type: 'ZEBRA_PRINT_FINISH',
            source: 'zebra',
          });
        });
    }
  }

  private toPrinterInfo(printer: WebLabel.LabelPrinterUsb): LabelPrinterInfo {
    const o = printer.printerOptions;

    return {
      name: this.getPrinterName(printer, true),
      type: this.type,
      serial: printer.printerSerial,
      uid: PrinterBase.getPrinterUid(this.type, printer.printerSerial),
      config: {
        serial: printer.printerSerial,
        speed: o.speed.printSpeed,
        darkness: o.darknessPercent,
        width: o.mediaWidthDots,
        autosense: false,
      },
    };
  }

  private getPrinterName(p: WebLabel.LabelPrinterUsb, short = false): string {
    return short
      ? `Label (#${p.printerSerial})`
      : `${p.printerManufacturer} ${p.printerModel} (S/N: ${p.printerSerial})`;
  }

  private async _testPrint(printer: WebLabel.LabelPrinterUsb): Promise<void> {
    const doc = WebLabel.ReadyToPrintDocuments.printTestLabelDocument(
      printer.printerOptions.mediaWidthDots,
    );

    await printer.sendDocument(doc);
  }

  private async _print(
    printer: WebLabel.LabelPrinterUsb,
    badge: BadgeData,
    design: BadgeDesign,
    reason?: string,
  ): Promise<void> {
    const printArea = document.getElementById('printArea')!;

    createRoot(printArea).render(
      <Provider store={store}>
        <TranslationProvider>
          <BadgePageRender labels={[badge]} nodes={transformNodeMap(design)} />
        </TranslationProvider>
      </Provider>,
    );

    // Allows time for OccamyText to find best font size
    await delayPromise(200);

    // Add a small margin as printer alignment is not exact.
    const width = printer.printerOptions.mediaWidthDots - 2;
    const height = printer.printerOptions.mediaLengthDots - 2;

    /*
     *  The printArea object is not rendered so will have a size of zero.
     *  Get the size of the preview object instead.
     * const rect = printArea.getElementsByClassName('badgeLabel')[0]!.getBoundingClientRect();
     */
    const rect = document.querySelector('#dashboard .badgeLabel')?.getBoundingClientRect();

    const scale = rect === undefined ? 1 : Math.min(width / rect.width, height / rect.height);

    const topDiv = printArea.firstElementChild!.cloneNode(true) as Element;
    topDiv.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');

    const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
    foreignObject.setAttribute('width', '100%');
    foreignObject.setAttribute('height', '100%');
    foreignObject.setAttribute('transform', `scale(${scale})`);
    foreignObject.append(topDiv);

    const defsStyle = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    defsStyle.innerHTML = await getSVGFontStyle(design.content);
    foreignObject.append(topDiv);

    const svg = document.createElement('svg');
    svg.setAttribute('width', width.toString());
    svg.setAttribute('height', height.toString());
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.append(defsStyle);
    svg.append(foreignObject);

    const builder = printer.getLabelDocument();
    const fromSvg = await builder.addImageFromSVG(svg.outerHTML, width, height);
    const doc = fromSvg.addPrintCmd().finalize();

    await printer.sendDocument(doc);

    if (badge.userId) {
      const barcode = new URLSearchParams(location.search).get('barcode') ?? undefined;
      await api.markRegistrationBadgeAsPrinted(badge.userId, null, barcode, reason);
    }
  }
}
